import { useState, useEffect } from 'react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import { Icon } from '@iconify/react'
import FormLoader from '../../../../components/loader/FormLoader'
import { useNavigate } from 'react-router-dom'
import Pagination from '../../../../components/pagination/Pagination'
import FullLoader from '../../../../components/loader/FullLoader'
import Select from 'react-select'
import Statuses from '../../../../assets/data/Statuses'
import TagChips from '../../../../components/tags/TagChips'
import TagFilterSelect from '../../../../components/tags/TagFilterSelect'
import '../../../../components/tags/tags.scss'
import { BOOKING_LOCATION_FILTERS } from '../../../../utils/locations'

interface FetchOpts {
  page?: number
  pageSize?: number
  search?: string
  from?: string
  to?: string
  deliveryType?: string[]
  status?: string[]
  creatorId?: string
  userId?: string
  format?: 'csv' | 'excel'
  location?: string[]
  tags?: string[]
}

function AllBookings() {
  const baseURL = (import.meta as any).env.VITE_BASE_URL
  const { notify, tagsById } = useGlobalContext()
  const navigate = useNavigate()
  const [bookings, setBookings] = useState<any>({ data: [] })
  const [bookingsLoading, setBookingsLoading] = useState(true)
  const [paginationPage, setPaginationPage] = useState('default')
  const [search, setSearch] = useState('')
  const [selectedDeliveryTypes, setDeliveryTypes] = useState<string[]>([])
  const [selectedStatuses, setStatuses] = useState<string[]>([])
  const [location, setLocation] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  function buildQueryString(opts: FetchOpts) {
    const params = new URLSearchParams()
    if (opts.page != null) params.set('page', opts.page.toString())
    if (opts.pageSize != null) params.set('limit', opts.pageSize.toString())
    if (search) params.set('search', search)
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.creatorId) params.set('creatorId', opts.creatorId)
    if (opts.userId) params.set('userId', opts.userId)
    if (opts.format) params.set('format', opts.format)
    if (selectedDeliveryTypes.length > 0) {
      selectedDeliveryTypes.forEach((dt) => params.append('deliveryType', dt))
    }
    if (opts.status) {
      opts.status.forEach((st) => params.append('status', st))
    }
    if (location.length > 0) {
      location.forEach((dt) => params.append('location', dt))
    }
    if (selectedTags.length > 0) {
      selectedTags.forEach((tagId) => params.append('tags', tagId))
    }
    return params.toString()
  }

  async function fetchBookings(opts: FetchOpts) {
    setBookingsLoading(true)
    const qs = buildQueryString(opts)

    const url = `admin/all-bookings?${qs}`
    try {
      const result: any = await get(url)
      if (result.status === 200) {
        setBookings(result)
      } else {
        notify('error', result.message)
      }
    } catch (err) {
      notify('error', 'Could not fetch bookings')
    } finally {
      setBookingsLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings({ page: 1, pageSize: 10 })
  }, [])

  //Filter
  const [dates, setDates] = useState({
    from: '',
    to: '',
  })

  //<----------- Download Excel and CSV --------------->
  const [downloading, setDownloading] = useState(false)

  async function handleDownload(format: 'csv' | 'excel') {
    setDownloading(true)
    const qs = buildQueryString({
      from: dates.from,
      to: dates.to,
      search,
      deliveryType: selectedDeliveryTypes,
      status: selectedStatuses,
      format,
      location,
    })
    const token = sessionStorage.getItem('token')
    const url = `${baseURL}/admin/all-bookings/export?${qs}`

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const fileName = `Bookings_${dates.from}_to_${dates.to}`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      notify('error', 'Could not download bookings')
    } finally {
      setDownloading(false)
    }
  }

  // Download Manifest
  async function handleDownloadManifest() {
    const qs = buildQueryString({
      from: dates.from,
      to: dates.to,
      format: 'excel',
      location,
    })
    const token = sessionStorage.getItem('token')
    const url = `${baseURL}/admin/manifest/export?${qs}`

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()

      const fileName = `Manifest_${dates.from}_to_${dates.to}_for_${location}`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      notify('error', 'Could not download bookings')
    }
  }
  return (
    <div className='home'>
      {downloading && <FullLoader />}
      <div className='home_tables'>
        <div className='left'>
          <div className='filter'>
            <div className='flex gap-17 items-center '>
              <Select
                isMulti
                options={[
                  { value: 'EXPORT', label: 'Export' },
                  { value: 'DOMESTIC', label: 'Domestic' },
                  { value: 'IMPORT', label: 'Import' },
                ]}
                placeholder='Delivery Type'
                onChange={(e) => {
                  const values = e.map((item) => item.value)
                  setDeliveryTypes(values)
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: '#446ce4',
                    borderRadius: '32px',
                  }),
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                  }),
                  multiValue: (baseStyles) => ({
                    ...baseStyles,
                    background: 'none',
                  }),
                  multiValueLabel: (baseStyles) => ({
                    ...baseStyles,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                  }),
                }}
              />
              <Select
                isMulti
                options={Statuses.map((status) => ({
                  value: status.code,
                  label: status.label,
                }))}
                placeholder='Status'
                onChange={(e) => {
                  const values = e.map((item) => item.value)
                  setStatuses(values)
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: '#446ce4',
                    borderRadius: '32px',
                  }),
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                  }),
                  multiValue: (baseStyles) => ({
                    ...baseStyles,
                    background: 'none',
                  }),
                  multiValueLabel: (baseStyles) => ({
                    ...baseStyles,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                  }),
                }}
              />
              <TagFilterSelect
                value={selectedTags}
                onChange={setSelectedTags}
              />
              <Select
                isMulti
                options={BOOKING_LOCATION_FILTERS}
                placeholder='Location'
                onChange={(e) => {
                  const values = e.map((item) => item.value)
                  setLocation(values)
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: '#446ce4',
                    borderRadius: '32px',
                  }),
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                  }),
                  multiValue: (baseStyles) => ({
                    ...baseStyles,
                    background: 'none',
                  }),
                  multiValueLabel: (baseStyles) => ({
                    ...baseStyles,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                  }),
                }}
              />
              <input
                type='date'
                name='from'
                value={dates.from}
                onChange={(e) => setDates({ ...dates, from: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
              />
              <p>to</p>
              <input
                type='date'
                name='to'
                value={dates.to}
                onChange={(e) => setDates({ ...dates, to: e.target.value })}
                min={dates.from}
                max={new Date().toISOString().split('T')[0]}
              />

              <button
                disabled={Boolean(dates.from) !== Boolean(dates.to)}
                onClick={() => {
                  setPaginationPage('date')
                  fetchBookings({
                    page: 1,
                    pageSize: bookings.pagination.limit,
                    from: dates.from,
                    to: dates.to,
                    deliveryType: selectedDeliveryTypes,
                    status: selectedStatuses,
                    location,
                  })
                }}
              >
                Filter
              </button>
              {dates.from && dates.to && location && (
                <button
                  disabled={Boolean(dates.from) !== Boolean(dates.to)}
                  onClick={() => {
                    handleDownloadManifest()
                  }}
                >
                  Download Manifest
                </button>
              )}
            </div>
          </div>

          {bookingsLoading ? (
            <FormLoader position='center' />
          ) : (
            <>
              <div className='flex justify-between  search-btns flex-wrap'>
                <div className='search '>
                  <div className='flex mb-15'>
                    <input
                      type='search'
                      name=''
                      id=''
                      value={search}
                      placeholder='search by name or tracking Id or booking Id'
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <button
                      onClick={() => {
                        fetchBookings({ page: 1, pageSize: 10 })
                      }}
                    >
                      Search
                    </button>
                  </div>
                </div>

                {paginationPage === 'date' && (
                  <div className='buttons flex'>
                    <button onClick={() => handleDownload('csv')}>
                      Export CSV
                    </button>
                    <button onClick={() => handleDownload('excel')}>
                      Export EXCEL
                    </button>
                  </div>
                )}
              </div>
              {bookings.data.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {bookings.data.map((item: any, i: number) => (
                      <div
                        className='card'
                        onClick={() => navigate(`/admin/booking/${item.id}`)}
                        key={item.id}
                      >
                        <div className='flex justify-between row'>
                          <div className='count flex items-center justify-center'>
                            {i + 1}
                          </div>
                          <p> {new Date(item.dateTime).toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Amount</h4>
                          <p>₦{item.amount.toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Tracking ID</h4>
                          <p> {item.trackingId}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Delivery type</h4>
                          <p>{item.deliveryType}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Status</h4>
                          <span className={`chip ${item.status.code}`}>
                            {item.status.description}
                          </span>
                        </div>
                        {item.tags?.length > 0 && (
                          <div className='row flex justify-between'>
                            <h4>Tags</h4>
                            <TagChips
                              tagIds={item.tags}
                              tagsById={tagsById}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Date/Time</th>
                        <th>Creator Name</th>
                        <th>Amount</th>
                        <th>Tracking ID</th>
                        <th>Delivery Type</th>
                        <th>Channel</th>
                        <th>Status</th>
                        <th>Tags</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.data.map((item: any, i: number) => (
                        <tr key={item.id}>
                          <td>{i + 1}</td>
                          <td className='count'>
                            {new Date(item.dateTime).toLocaleString()}
                          </td>
                          <td className='count'>{item.creatorName}</td>
                          <td className='count'>
                            NGN {item.amount.toLocaleString()}
                          </td>
                          <td className='count'>{item.trackingId}</td>
                          <td className='count'>{item.deliveryType}</td>
                          <td className='count'>{item.channel}</td>
                          <td>
                            <span className={`chip ${item.status.code}`}>
                              {item.status.description}
                            </span>
                          </td>
                          <td>
                            <TagChips
                              tagIds={item.tags}
                              tagsById={tagsById}
                              empty='—'
                            />
                          </td>
                          <td>
                            <span
                              onClick={() =>
                                navigate(`/admin/booking/${item.id}`)
                              }
                              className='view'
                            >
                              view
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={bookings.pagination.page}
                    totalCount={bookings.pagination.totalPages}
                    pageSize={bookings.pagination.limit}
                    onPageChange={(page: number, pageSize: number) =>
                      fetchBookings({
                        page,
                        pageSize,
                        search,
                        from: dates.from,
                        to: dates.to,
                        deliveryType: selectedDeliveryTypes,
                        status: selectedStatuses,
                      })
                    }
                  />
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='majesticons:book-line' />
                    <h1>No Bookings To Show</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AllBookings
